import { Link, useForm, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Button,
  Group,
  NavLink,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core'
import { FileText, FolderOpen, Plus } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import CenteredEmptyState from '@/components/CenteredEmptyState'
import MarkdownBody from '@/components/MarkdownBody'
import SidebarLayout from '@/components/SidebarLayout'
import type { ProjectProps, WikiPageProps, WikiPageSummaryProps } from '@/types'

type WikiLayoutProps = {
  project: ProjectProps
  wikiPage: WikiPageProps | null
  wikiPages: WikiPageSummaryProps[]
}

type TreeNode = {
  name: string
  fullPath: string
  page?: WikiPageSummaryProps
  children: Map<string, TreeNode>
}

function buildPageTree(pages: WikiPageSummaryProps[]): TreeNode {
  const root: TreeNode = { name: '', fullPath: '', children: new Map() }

  for (const page of pages) {
    const parts = page.path.split('/').filter(Boolean)
    let current = root

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      const fullPath = parts.slice(0, i + 1).join('/')
      if (!current.children.has(part)) {
        current.children.set(part, { name: part, fullPath, children: new Map() })
      }
      current = current.children.get(part)!
    }
    current.page = page
  }

  return root
}

function PageTreeNode({
  node,
  projectId,
  activePageId,
  onNavigate,
}: {
  node: TreeNode
  projectId: string
  activePageId: string | null
  onNavigate?: () => void
}) {
  const children = Array.from(node.children.values())
  const hasChildren = children.length > 0
  const isActive = node.page?.id === activePageId

  if (node.page && !hasChildren) {
    return (
      <NavLink
        component={Link}
        href={`/projects/${projectId}/wiki_pages/${node.page.id}`}
        label={node.page.title || node.name}
        leftSection={<FileText size={16} />}
        active={isActive}
        onClick={onNavigate}
      />
    )
  }

  if (node.page) {
    return (
      <NavLink
        component={Link}
        href={`/projects/${projectId}/wiki_pages/${node.page.id}`}
        label={node.page.title || node.name}
        leftSection={<FileText size={16} />}
        active={isActive}
        defaultOpened
        onClick={onNavigate}
      >
        {children.map((child) => (
          <PageTreeNode
            key={child.fullPath}
            node={child}
            projectId={projectId}
            activePageId={activePageId}
            onNavigate={onNavigate}
          />
        ))}
      </NavLink>
    )
  }

  // Folder node (no page)
  return (
    <NavLink
      label={node.name}
      leftSection={<FolderOpen size={16} />}
      defaultOpened
    >
      {children.map((child) => (
        <PageTreeNode
          key={child.fullPath}
          node={child}
          projectId={projectId}
          activePageId={activePageId}
          onNavigate={onNavigate}
        />
      ))}
    </NavLink>
  )
}


function WikiPageForm({
  projectId,
  wikiPage,
  onCancel,
}: {
  projectId: string
  wikiPage?: WikiPageProps
  onCancel: () => void
}) {
  const { csrf_token } = usePage().props
  const isEdit = !!wikiPage

  const { data, setData, post, patch, processing, reset } = useForm({
    wiki_page: {
      title: wikiPage?.title ?? '',
      path: wikiPage?.path ?? '',
      body: wikiPage?.body ?? '',
    },
    authenticity_token: csrf_token as string,
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      patch(`/projects/${projectId}/wiki_pages/${wikiPage.id}`, {
        preserveScroll: true,
        onSuccess: () => onCancel(),
      })
    } else {
      post(`/projects/${projectId}/wiki_pages`, {
        preserveScroll: true,
        onSuccess: () => {
          reset()
          onCancel()
        },
      })
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <Paper component="form" onSubmit={handleSubmit} p="lg" withBorder>
        <Stack gap="md">
          {!isEdit && <Title order={3}>New wiki page</Title>}
          <TextInput
            label="Title"
            value={data.wiki_page.title}
            onChange={(e) => setData('wiki_page', { ...data.wiki_page, title: e.target.value })}
            required={!isEdit}
          />
          <TextInput
            label="Path"
            placeholder={isEdit ? undefined : 'e.g. guides/setup.md'}
            value={data.wiki_page.path}
            onChange={(e) => setData('wiki_page', { ...data.wiki_page, path: e.target.value })}
            required={!isEdit}
          />
          <Textarea
            label="Body"
            autosize
            minRows={12}
            value={data.wiki_page.body}
            onChange={(e) => setData('wiki_page', { ...data.wiki_page, body: e.target.value })}
          />
          <Group>
            <Button type="submit" loading={processing}>{isEdit ? 'Save' : 'Create'}</Button>
            <Button type="button" variant="default" onClick={() => { reset(); onCancel() }}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Paper>
    </div>
  )
}

export default function WikiLayout({ project, wikiPage, wikiPages }: WikiLayoutProps) {
  const [editing, setEditing] = useState(false)
  const [creating, setCreating] = useState(false)
  const isMarkdown = wikiPage?.path?.toLowerCase().endsWith('.md')

  const tree = useMemo(() => buildPageTree(wikiPages), [wikiPages])
  const treeChildren = Array.from(tree.children.values())

  return (
    <SidebarLayout sidebar={({ closeSidebar }) => (
      <>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm" lineClamp={1}>{project.name}</Text>
            <ActionIcon
              variant="light"
              size="sm"
              onClick={() => { setCreating(true); setEditing(false) }}
              title="New page"
            >
              <Plus size={14} />
            </ActionIcon>
          </Group>
        </div>

        <ScrollArea style={{ flex: 1 }}>
          <Stack gap={0} p="xs">
            {treeChildren.length > 0 ? (
              treeChildren.map((node) => (
                <PageTreeNode
                  key={node.fullPath}
                  node={node}
                  projectId={project.id}
                  activePageId={wikiPage?.id ?? null}
                  onNavigate={closeSidebar}
                />
              ))
            ) : (
              <Text size="sm" c="dimmed" p="sm">No pages yet.</Text>
            )}
          </Stack>
        </ScrollArea>
      </>
    )}>
      <ScrollArea style={{ flex: 1 }}>
        {creating ? (
          <WikiPageForm
            projectId={project.id}
            onCancel={() => setCreating(false)}
          />
        ) : editing && wikiPage ? (
          <WikiPageForm
            projectId={project.id}
            wikiPage={wikiPage}
            onCancel={() => setEditing(false)}
          />
        ) : wikiPage ? (
          <div style={{ padding: 24 }}>
            <Paper p="xl" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Text c="dimmed" size="sm">{wikiPage.path}</Text>
                  <Button variant="default" size="sm" onClick={() => { setEditing(true); setCreating(false) }}>
                    Edit
                  </Button>
                </Group>

                <Title order={2}>{wikiPage.title}</Title>

                {isMarkdown && wikiPage.body ? (
                  <MarkdownBody>{wikiPage.body}</MarkdownBody>
                ) : (
                  <Text component="pre" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {wikiPage.body || '(empty)'}
                  </Text>
                )}
              </Stack>
            </Paper>
          </div>
        ) : (
          <CenteredEmptyState
            icon={FileText}
            title="No wiki pages yet"
            description="Create the first page using the + button in the sidebar."
          />
        )}
      </ScrollArea>
    </SidebarLayout>
  )
}
